import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { Badge, Banner, Button, Card, EmptyState, Field, Mono, PageBody, PageHeader, Select } from "../ui/Field";

type PageRow = {
  id: string;
  parent_id: string | null;
  slug: string;
  title: string;
  status: string;
  sort_order: number;
  is_template: number;
  updated_at: number;
};

type Node = PageRow & { children: Node[] };

export default function PagesList() {
  const { slug: siteSlug } = useParams();
  const [items, setItems] = useState<PageRow[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newParent, setNewParent] = useState<string>("");

  const load = () => api.get<{ items: PageRow[] }>(`/api/sites/${siteSlug}/pages`).then((r) => setItems(r.items)).catch((e) => setErr((e as Error).message));
  useEffect(() => { if (siteSlug) load(); }, [siteSlug]);

  const tree = useMemo(() => buildTree(items), [items]);

  const [fromTemplate, setFromTemplate] = useState<string>("");

  async function createPage(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    try {
      const created = await api.post<{ id: string }>(`/api/sites/${siteSlug}/pages`, {
        title: newTitle,
        slug: newSlug || slugify(newTitle),
        parent_id: newParent || null,
      });
      if (fromTemplate) {
        try {
          const tmpl = await api.get<{ revision: { metadata: object; header: object; footer: object; blocks: unknown[] } | null }>(`/api/sites/${siteSlug}/pages/${fromTemplate}`);
          if (tmpl.revision) {
            await api.put(`/api/sites/${siteSlug}/pages/${created.id}/draft`, {
              metadata: { ...tmpl.revision.metadata, title: newTitle, slug: newSlug || slugify(newTitle) },
              header: tmpl.revision.header,
              footer: tmpl.revision.footer,
              blocks: tmpl.revision.blocks,
            });
          }
        } catch { /* ignore */ }
      }
      setShowNew(false);
      setNewTitle("");
      setNewSlug("");
      setNewParent("");
      setFromTemplate("");
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : (e as Error).message);
    }
  }

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 01 · Pages"
        title={<>Site <em>pages</em></>}
        description="Hierarchical tree of pages with their slugs, statuses, and templates. Drill in to edit content, header, footer, and metadata."
        actions={<Button onClick={() => setShowNew((s) => !s)}>{showNew ? "Cancel" : "+ New page"}</Button>}
      />
      {err && <Banner kind="error">{err}</Banner>}

      {showNew && (
        <Card number="P0" title="Create page" className="mb-6">
          <form onSubmit={createPage}>
            <Field label="Title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} required autoFocus />
            <Field label="Slug" value={newSlug} onChange={(e) => setNewSlug(e.target.value)} placeholder={slugify(newTitle) || "url-segment"} />
            <Select label="Parent (optional)" value={newParent} onChange={(e) => setNewParent(e.target.value)}>
              <option value="">(root)</option>
              {items.filter((p) => !p.slug.startsWith("__")).map((p) => (
                <option key={p.id} value={p.id}>{p.title} (/{p.slug})</option>
              ))}
            </Select>
            {items.some((p) => p.is_template) && (
              <Select label="From template (optional)" value={fromTemplate} onChange={(e) => setFromTemplate(e.target.value)}>
                <option value="">Blank</option>
                {items.filter((p) => p.is_template).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
              </Select>
            )}
            <div className="flex gap-2 mt-2">
              <Button type="submit">Create</Button>
              <Button type="button" variant="ghost" onClick={() => setShowNew(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      <Card number="P1" title={`Tree (${items.length})`} bodyClassName={tree.length === 0 ? "p-5" : "p-0"}>
        {tree.length === 0 ? (
          <EmptyState title="No pages yet." description="Create your first page to start composing content." />
        ) : (
          <TreeView nodes={tree} siteSlug={siteSlug!} depth={0} />
        )}
      </Card>
    </PageBody>
  );
}

function TreeView({ nodes, siteSlug, depth }: { nodes: Node[]; siteSlug: string; depth: number }) {
  return (
    <ul className="list-none p-0 m-0">
      {nodes.map((n) => (
        <li key={n.id}>
          <div
            className="flex items-center justify-between gap-4 py-3 px-5 border-b border-[var(--color-rule)] last:border-0 hover:bg-[var(--color-paper-2)] transition-colors"
            style={{ paddingLeft: `${depth * 20 + 20}px` }}
          >
            <Link
              to={`/sites/${siteSlug}/pages/${n.id}`}
              className={`no-underline flex items-baseline gap-2 flex-1 ${n.slug.startsWith("__") ? "text-[var(--color-muted)]" : "text-[var(--color-ink)]"} hover:text-[var(--color-accent)] transition-colors`}
            >
              <span className="text-[14px] font-medium">{n.title}</span>
              <Mono className="text-[11.5px] text-[var(--color-muted)]">/{n.slug}</Mono>
              {n.is_template ? <Badge tone="accent">Template</Badge> : null}
            </Link>
            <Badge tone={n.status === "published" ? "success" : n.status === "review" ? "warning" : "neutral"}>{n.status}</Badge>
          </div>
          {n.children.length > 0 && <TreeView nodes={n.children} siteSlug={siteSlug} depth={depth + 1} />}
        </li>
      ))}
    </ul>
  );
}

function buildTree(items: PageRow[]): Node[] {
  const map = new Map<string, Node>();
  items.forEach((i) => map.set(i.id, { ...i, children: [] }));
  const roots: Node[] = [];
  map.forEach((n) => {
    if (n.parent_id && map.has(n.parent_id)) map.get(n.parent_id)!.children.push(n);
    else roots.push(n);
  });
  const sortRec = (xs: Node[]) => {
    xs.sort((a, b) => (a.sort_order - b.sort_order) || a.title.localeCompare(b.title));
    xs.forEach((x) => sortRec(x.children));
  };
  sortRec(roots);
  return roots;
}

function slugify(s: string) {
  return s.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60);
}
